import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Eye, Heart, Compass, Users } from 'lucide-react';

const Walezi = () => {
 const qualities = [
 {
 icon: <Eye size={28} />,
 title: 'Observers & Nurturers',
 description: 'Walezi watch, listen, and understand each child deeply. They notice patterns, identify needs, and respond with care and precision.'
 },
 {
 icon: <Heart size={28} />,
 title: 'Guides, Not Instructors',
 description: 'Walezi facilitate learning rather than delivering it. They ask questions, create conditions for discovery, and trust children to lead their own growth.'
 },
 {
 icon: <Compass size={28} />,
 title: 'Mastery Trackers',
 description: 'Walezi use technology and observation to track academic progress, but make all decisions about readiness, pacing, and support through human judgment.'
 },
 {
 icon: <Users size={28} />,
 title: 'Community Builders',
 description: 'Walezi create environments where children feel safe, seen, and valued. They model collaboration, kindness, and thoughtful communication.'
 }
 ];

 return (
 <div className="walezi-page">
 <section className="page-hero">
 <div className="section-container">
 <h1 className="page-title">Walezi</h1>
 <p className="page-subtitle">
 The educators who make Nova Roots possible.
 </p>
 </div>
 </section>

 <section className="walezi-intro-section">
 <div className="section-container">
 <div className="intro-content">
 <p className="intro-text">
 In Swahili, <em>walezi</em> means "those who nurture and raise". At Nova Roots, Walezi are 
 not traditional teachers. They are guides, observers, and learning architects who trust 
 children and create the conditions for deep, meaningful growth.
 </p>
 </div>
 </div>
 </section>

 <section className="qualities-section">
 <div className="section-container">
 <h2 className="section-title">What Walezi Do</h2>
 <div className="qualities-grid">
 {qualities.map((quality, index) => (
 <Card key={index} className="quality-card">
 <CardContent className="quality-content">
 <div className="quality-icon">
 {quality.icon}
 </div>
 <h3>{quality.title}</h3>
 <p>{quality.description}</p>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>
 </section>

 <section className="training-section">
 <div className="section-container">
 <h2 className="section-title">Deep Training & Development</h2>
 <div className="training-content">
 <Card className="training-card">
 <CardContent>
 <p>
 Nova Roots Walezi undergo rigorous training in child development, mastery-based pedagogy, 
 observation techniques, and technology-supported learning. They learn how to see each 
 child clearly, respond to individual needs, and create learning experiences that honour 
 curiosity and agency.
 </p>
 </CardContent>
 </Card>
 <Card className="training-card">
 <CardContent>
 <p>
 Training is ongoing. Walezi engage in regular professional development, peer observation, 
 and reflective practice. They are learners themselves—continuously refining their craft 
 and deepening their understanding of how children grow.
 </p>
 </CardContent>
 </Card>
 </div>
 </div>
 </section>

 <section className="philosophy-section">
 <div className="section-container">
 <div className="philosophy-box">
 <h2>Trust as Foundation</h2>
 <p>
 Nova Roots believes that exceptional education begins with exceptional educators. Walezi 
 are chosen for their intellectual curiosity, emotional intelligence, and deep respect for 
 children. They are trusted to make decisions, adapt to needs, and lead with wisdom rather 
 than scripts.
 </p>
 <p>
 When educators are seen, supported, and trusted, children thrive.
 </p>
 </div>
 </div>
 </section>

 <section className="difference-section">
 <div className="section-container">
 <h2 className="section-title">A Different Kind of Educator</h2>
 <div className="difference-content">
 <div className="difference-grid">
 <div className="difference-item">
 <h4>Not Lecturers</h4>
 <p>Walezi don't stand at the front delivering information. They move through the space, observe, and respond.</p>
 </div>
 <div className="difference-item">
 <h4>Not Disciplinarians</h4>
 <p>Walezi guide behaviour through relationship, clarity, and respect—not control or punishment.</p>
 </div>
 <div className="difference-item">
 <h4>Not Performers</h4>
 <p>Walezi focus on children's learning, not their own performance. Success is measured by child growth, not teacher output.</p>
 </div>
 <div className="difference-item">
 <h4>Not Curriculum Deliverers</h4>
 <p>Walezi use curriculum as a guide, but adapt based on what each child needs in the moment.</p>
 </div>
 </div>
 </div>
 </div>
 </section>
 </div>
 );
};

export default Walezi;